import { useDeviceSelector, useGameConfigurationSelector } from 'js/store/hooks';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import { defaultTo, includes, pick, pipe, prop } from 'ramda';
import * as hooks from '../../../../../../../common/Game/FavoriteIcon/hooks/useToggleFavorite';
import { useLottieAnimation } from '@theme/js/hooks/swiss/useLottieAnimation';
import FavIcon from '../../assets/Favourite_Icon.json';
import { FavoriteRoundIconProps } from '../types';

export const NOT_SUPPORTED_IOS_VERSION = '15.4';
export const IOS = 'ios';

export const checkIsSupportedLottieLib = (os: string, osVersion: string) => {
    if (os !== IOS) {
        return true;
    }

    return parseFloat(osVersion) - parseFloat(NOT_SUPPORTED_IOS_VERSION) > 0;
};

export const useFavoriteRoundIcon = ({
    gameFeed,
    code
}: FavoriteRoundIconProps) => {
    const isFavorite = useGameConfigurationSelector(pipe(
        prop<string[]>('favoriteGames'),
        defaultTo<string[]>([]),
        includes(code)
    ));
    const { osVersion, os } = useDeviceSelector(pick(['osVersion', 'os']));
    const [autoplay, setAutoplay] = useState(false);
    const { stop, play, dotLottieRefCallback } = useLottieAnimation();
     // Since it is not possible to get totalFrames from dotLottie ref on page render, we need to get frames right from the animation json file
     const totalFrames = FavIcon.op - 1;
     const [segments, setSegments] = useState<[number, number] | undefined>([totalFrames - 1, totalFrames]);

    const [isSupportedLottieLib, setIsSupportedLottieLib] = React.useState(checkIsSupportedLottieLib(os, osVersion));
    const isLottieIcon = isFavorite && isSupportedLottieLib;
    const isStaticIcon = isFavorite && !isSupportedLottieLib;

    const handleSetFavorite = useCallback(() => {
        // set segments to first and last frames
        setSegments([1, totalFrames - 1]);
        setAutoplay(true);
        if (isFavorite) {
            stop();
        }

        if (!isFavorite) {
            play();
        }
      }, [isFavorite, play, stop, totalFrames]);

    const toggleFavorite = hooks.useToggleFavorite({ code, isFavorite, gameFeed, isAnimatedFromInfoPopup: true });

    const onClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();

        toggleFavorite();
        handleSetFavorite();
    }, [toggleFavorite, handleSetFavorite]);

    useLayoutEffect(() => {
        const handleOrientationChange = () => {
            stop();
            setAutoplay(false);
            if (totalFrames) {
                // set segments to before last frame and last frame
              setSegments([totalFrames - 1, totalFrames]);
            }
        };

        window.addEventListener('resize', handleOrientationChange);

        return () => {
          window.removeEventListener('resize', handleOrientationChange);
        };
    }, [stop, totalFrames]);

    useLayoutEffect(() => {
        setIsSupportedLottieLib(checkIsSupportedLottieLib(os, osVersion));
    }, [os, osVersion]);

    return {
        isFavorite,
        autoplay,
        segments,
        onClick,
        dotLottieRefCallback,
        isLottieIcon,
        isStaticIcon
    };
};
